import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { imageSrc } from "@/pages/Products";
import { formatMoney } from "@/lib/format";
import { cacheGet, cachePut } from "@/lib/offline";
import { Ship, ArrowLeft, ExternalLink } from "lucide-react";

// Side-by-side comparison of 2-3 vessels (?ids=a,b,c). Rows are the union
// of principal-particular labels; key features listed underneath.
export default function VesselCompare() {
  const [params] = useSearchParams();
  const ids = useMemo(
    () => (params.get("ids") || "").split(",").filter(Boolean).slice(0, 3),
    [params],
  );
  const [all, setAll] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/vessels")
      .then(({ data }) => {
        if (!active) return;
        setAll(data);
        cachePut("vessels", data);
      })
      .catch(async (e) => {
        if (!e?.response) {
          const cached = await cacheGet("vessels");
          if (cached && active) {
            setAll(cached);
            toast.message("Offline — comparing from saved vessels");
            return;
          }
        }
        if (active) setAll([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const vessels = useMemo(
    () => ids.map((id) => (all || []).find((v) => v.id === id)).filter(Boolean),
    [all, ids],
  );

  // Union of spec labels in first-seen order (data rows only, with values).
  const specLabels = useMemo(() => {
    const labels = [];
    for (const v of vessels) {
      for (const s of v.principal_particulars || []) {
        if ((s.kind || "row") === "heading") continue;
        if (s.enabled === false) continue;
        const label = (s.label || "").trim();
        if (!label || !(s.value || "").trim()) continue;
        if (!labels.includes(label)) labels.push(label);
      }
    }
    return labels;
  }, [vessels]);

  const specValue = (v, label) => {
    const row = (v.principal_particulars || []).find(
      (s) => (s.kind || "row") !== "heading" && (s.label || "").trim() === label,
    );
    return row && (row.value || "").trim() ? row.value : "—";
  };

  if (all === null) {
    return <div className="p-10 text-[#5B6B7C] font-display tracking-wider text-sm">Loading…</div>;
  }

  const cols = vessels.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24" data-testid="vessel-compare-page">
      <Link
        to="/vessels"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[#5B6B7C] hover:text-[#011836] mb-3"
        data-testid="compare-back-to-vessels"
      >
        <ArrowLeft className="h-3 w-3" /> Back to vessels
      </Link>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836] mb-8">
        Compare Vessels
      </h1>

      {vessels.length < 2 ? (
        <div className="sos-surface rounded-2xl p-12 text-center">
          <Ship className="h-10 w-10 text-[#B6C2CD] mx-auto mb-3" />
          <div className="text-sm text-[#5B6B7C]">
            Pick two or three vessels on the Vessels page to compare them side by side.
          </div>
        </div>
      ) : (
        <div className="sos-surface rounded-2xl overflow-hidden">
          {/* Vessel headers */}
          <div className={`grid grid-cols-1 ${cols} border-b border-[#DDE5EC]`}>
            {vessels.map((v) => (
              <div
                key={v.id}
                className="p-5 border-b md:border-b-0 md:border-r border-[#DDE5EC] last:border-r-0 last:border-b-0"
                data-testid={`compare-col-${v.id}`}
              >
                <div className="aspect-[16/10] rounded-lg overflow-hidden bg-white border border-[#DDE5EC] relative mb-4">
                  {v.render_path ? (
                    <img
                      src={imageSrc(v.render_path)}
                      alt={v.name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Ship className="h-10 w-10 text-[#B6C2CD]" />
                    </div>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#0088CC] mb-1">
                  {v.sub_category || v.vessel_type || "Vessel"}
                </div>
                <div className="font-display text-lg font-bold text-[#011836] leading-tight">{v.name}</div>
                <div className="mt-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C] mr-2">From</span>
                  <span className="text-[#0088CC] font-display font-bold tabular-nums">
                    {v.price != null ? formatMoney(v.price, v.price_currency) : "—"}
                  </span>
                  {v.price != null && (
                    <span className="text-[10px] text-[#5B6B7C] ml-1">{v.price_currency || "USD"}</span>
                  )}
                </div>
                <Link
                  to={`/vessels/${v.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0088CC] hover:text-[#011836]"
                  data-testid={`compare-view-vessel-${v.id}`}
                >
                  View vessel <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>

          {/* Spec matrix */}
          {specLabels.length > 0 && (
            <div>
              <div className="px-5 py-3 text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C] border-b border-[#DDE5EC]">
                Principal Particulars
              </div>
              {specLabels.map((label) => (
                <div key={label} className="border-b border-[#E8EEF3] last:border-b-0" data-testid={`compare-spec-${label}`}>
                  <div className="px-5 pt-3 text-[10px] uppercase tracking-wider text-[#5B6B7C]">{label}</div>
                  <div className={`grid grid-cols-1 ${cols}`}>
                    {vessels.map((v) => (
                      <div
                        key={v.id}
                        className="px-5 py-2 md:border-r border-[#E8EEF3] last:border-r-0 text-sm text-[#011836]"
                      >
                        {specValue(v, label)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
